// packages/frontend/src/components/FilePreviewModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Download,
  Maximize2,
  Minimize2,
  Edit,
  ZoomIn,
  ZoomOut,
  RotateCw,
  FileText,
  Image as ImageIcon,
  Video,
  Music,
  FileCode,
  File as FileIcon
} from 'lucide-react';
import { useToastStore } from '../stores/toastStore';
import { useAuthStore } from '../stores/authStore';
import { formatFileSize } from '../utils/api';
import { publicFileUrl, WORKER_URL } from '../config';

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileId: string;
  fileName: string;
  filePath: string;
  mimeType: string;
  fileSize: number;
  isPublic: boolean;
  isEditable?: boolean;
  onEdit?: () => void;
}

// Extensions that should always be previewed as plain text, regardless of the
// MIME type the server happens to report (shell/batch scripts often arrive as
// application/x-sh or text/shell, both unreliable across browsers).
const TEXT_EXTENSIONS = [
  '.md', '.yml', '.yaml', '.xml', '.svg', '.ini', '.conf', '.config',
  '.env', '.log', '.toml', '.cfg', '.properties',
  '.sh', '.bash', '.zsh', '.fish', '.ksh', '.csh',
  '.bat', '.cmd', '.ps1', '.psm1', '.psd1',
  '.dockerfile', '.makefile', '.gradle'
];

// File type detection
const getFileType = (mimeType: string, fileName: string): string => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'pdf';
  const lower = fileName.toLowerCase();
  if (mimeType.startsWith('text/') ||
      mimeType === 'application/json' ||
      mimeType === 'application/javascript' ||
      TEXT_EXTENSIONS.some(ext => lower.endsWith(ext))) return 'text';
  return 'unknown';
};

// Language detection for syntax highlighting
const getLanguageFromFileName = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  const languageMap: Record<string, string> = {
    'js': 'javascript', 'jsx': 'javascript', 'ts': 'typescript', 'tsx': 'typescript',
    'html': 'html', 'htm': 'html', 'css': 'css', 'scss': 'scss', 'sass': 'scss',
    'json': 'json', 'xml': 'xml', 'php': 'php', 'py': 'python', 'rb': 'ruby',
    'go': 'go', 'java': 'java', 'cpp': 'cpp', 'c': 'c', 'h': 'c', 'sql': 'sql',
    'sh': 'shell', 'bash': 'shell', 'yml': 'yaml', 'yaml': 'yaml', 'md': 'markdown',
    'txt': 'plaintext', 'log': 'plaintext', 'ini': 'ini', 'conf': 'plaintext'
  };
  return languageMap[ext || ''] || 'plaintext';
};

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  fileId,
  fileName,
  filePath,
  mimeType,
  fileSize,
  isPublic,
  isEditable,
  onEdit
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);
  const [textContent, setTextContent] = useState('');
  const [isLoadingText, setIsLoadingText] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const { addToast } = useToastStore();
  const { token } = useAuthStore();

  const fileType = getFileType(mimeType, fileName);
  const language = getLanguageFromFileName(fileName);
  // Public asset URL — reroutes amp/* to the active AMP subdomain when known.
  const fileUrl = publicFileUrl(filePath);

  // Load text content for text files
  useEffect(() => {
    if (isOpen && fileType === 'text') {
      loadTextContent();
    }
  }, [isOpen, fileId, fileType]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape to close
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
      
      // F11 to toggle fullscreen
      if (e.key === 'F11') {
        e.preventDefault();
        setIsFullscreen(!isFullscreen);
      }
      
      // Image controls
      if (fileType === 'image') {
        if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          zoomIn();
        }
        if (e.key === '-') {
          e.preventDefault();
          zoomOut();
        }
        if (e.key === 'r' || e.key === 'R') {
          e.preventDefault();
          rotateImage();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isFullscreen, fileType]);

  const loadTextContent = async () => {
    setIsLoadingText(true);
    setError(null);
    
    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${WORKER_URL}/api/files/${fileId}/content`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Failed to load file content: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to load file content');
      }
      
      setTextContent(result.data.content || '');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load file';
      setError(errorMessage);
      addToast({
        type: 'error',
        message: `Preview failed: ${errorMessage}`
      });
    } finally {
      setIsLoadingText(false);
    }
  };

  const handleDownload = () => {
    const downloadUrl = `${fileUrl}?download=1`;
    window.open(downloadUrl, '_blank');
  };

  const zoomIn = () => {
    setImageZoom(prev => Math.min(prev * 1.2, 5));
  };

  const zoomOut = () => {
    setImageZoom(prev => Math.max(prev / 1.2, 0.1));
  };

  const resetZoom = () => {
    setImageZoom(1);
    setImageRotation(0);
  };

  const rotateImage = () => {
    setImageRotation(prev => (prev + 90) % 360);
  };

  const getFileIcon = () => {
    switch (fileType) {
      case 'image': return <ImageIcon className="w-5 h-5" />;
      case 'video': return <Video className="w-5 h-5" />;
      case 'audio': return <Music className="w-5 h-5" />;
      case 'text': return <FileCode className="w-5 h-5" />;
      case 'pdf': return <FileText className="w-5 h-5" />;
      default: return <FileIcon className="w-5 h-5" />;
    }
  };

  const renderPreviewContent = () => {
    if (error) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <FileIcon className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-400 mb-2">Preview Failed</h3>
            <p className="text-dark-400 mb-4">{error}</p>
            <button onClick={handleDownload} className="btn btn-primary">
              <Download className="w-4 h-4 mr-2" />
              Download File
            </button>
          </div>
        </div>
      );
    }

    switch (fileType) {
      case 'image':
        return (
          <div className="flex-1 flex items-center justify-center overflow-hidden bg-dark-900/50">
            <div className="relative">
              <img
                src={fileUrl}
                alt={fileName}
                className="max-w-full max-h-full object-contain transition-transform duration-200"
                style={{
                  transform: `scale(${imageZoom}) rotate(${imageRotation}deg)`,
                  cursor: imageZoom > 1 ? 'move' : 'zoom-in'
                }}
                onClick={() => imageZoom === 1 && zoomIn()}
                onError={() => setError('Failed to load image')}
              />
            </div>
          </div>
        );

      case 'video':
        return (
          <div className="flex-1 flex items-center justify-center bg-black">
            <video
                ref={videoRef}
                src={fileUrl}
                controls
                className="max-w-full max-h-full"
                onError={() => setError('Failed to load video')}
                >
              Your browser does not support video playback.
            </video>
          </div>
        );

      case 'audio':
        return (
          <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-purple-900/20 to-blue-900/20">
            <div className="text-center">
              <div className="w-32 h-32 bg-dark-700 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Music className="w-16 h-16 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{fileName}</h3>
              <p className="text-dark-400 mb-6">{formatFileSize(fileSize)}</p>      
              <audio
                    ref={audioRef}
                    src={fileUrl}
                    controls
                    className="w-full max-w-md"
                    onError={() => setError('Failed to load audio')}
                    />
            </div>
          </div>
        );

      case 'pdf':
        return (
          <div className="flex-1">
            <iframe
              src={`${fileUrl}#toolbar=1`}
              className="w-full h-full border-0"
              title={fileName}
            />
          </div>
        );

      case 'text':
        return (
          <div className="flex-1 overflow-hidden">
            {isLoadingText ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-l-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-dark-400">Loading content...</p>
                </div>
              </div>
            ) : (
              <div className="h-full overflow-auto bg-dark-900/50">
                <pre className="p-6 text-sm font-mono whitespace-pre-wrap text-gray-300 leading-relaxed">
                  <code className={`language-${language}`}>{textContent}</code>
                </pre>
              </div>
            )}
          </div>
        );

      default:
        // For binary / non-previewable types, embed the backend's Google Drive-style
        // viewer page directly. The Worker serves a dark HTML page with the file
        // icon + metadata + download button — same UX whether opened in this modal
        // or in a new tab. No auto-download is triggered (binaries skip R2 entirely
        // on the server side until the user clicks Download).
        return (
          <div className="flex-1 bg-black">
            <iframe
              src={fileUrl}
              className="w-full h-full border-0"
              title={fileName}
              sandbox="allow-same-origin"
            />
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 bg-black/90 backdrop-blur-sm ${isFullscreen ? 'p-0' : 'p-4'}`}>
      <div className={`bg-dark-800 border border-dark-600 shadow-2xl flex flex-col ${
        isFullscreen 
          ? 'w-full h-full rounded-none' 
          : 'w-full max-w-6xl h-[90vh] mx-auto mt-4 rounded-xl overflow-hidden'
      }`}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-600 bg-dark-900/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-500/20 rounded-lg text-primary-400">
              {getFileIcon()}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{fileName}</h2>
              <div className="flex items-center gap-4 text-sm text-dark-400">
                <span>{formatFileSize(fileSize)}</span>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  isPublic ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {isPublic ? 'Public' : 'Private'}
                </span>
                <span className="text-xs text-dark-500">
                  {mimeType}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Image Controls */}
            {fileType === 'image' && (
              <>
                <button
                  onClick={zoomOut}
                  className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
                  title="Zoom Out (-)"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-sm text-dark-400 min-w-[60px] text-center">
                  {Math.round(imageZoom * 100)}%
                </span>
                <button
                  onClick={zoomIn}
                  className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
                  title="Zoom In (+)"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  onClick={rotateImage}
                  className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
                  title="Rotate (R)"
                >
                  <RotateCw className="w-4 h-4" />
                </button>
                <button
                  onClick={resetZoom}
                  className="px-3 py-2 text-sm bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
                  title="Reset View"
                >
                  Reset
                </button>
              </>
            )}
            
            {/* Edit Button */}
            {isEditable && onEdit && (
              <button
                onClick={onEdit}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                title="Edit File"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
            )}
            
            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              title="Download File"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            
            {/* Fullscreen Toggle */}
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
              title="Toggle Fullscreen (F11)"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            
            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
              title="Close (Esc)"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        {renderPreviewContent()}

        {/* Footer/Status Bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-dark-900 border-t border-dark-600 text-xs">
          <div className="flex items-center gap-4 text-dark-400">
            <span>Type: {mimeType}</span>
            {fileType === 'text' && textContent && (
              <>
                <span>Lines: {textContent.split('\n').length}</span>
                <span>Characters: {textContent.length}</span>
              </>
            )}
            {fileType === 'image' && (
              <span>Zoom: {Math.round(imageZoom * 100)}% • Rotation: {imageRotation}°</span>
            )}
          </div>
          
          <div className="flex items-center gap-4 text-dark-400">
            <span>Press Esc to close</span>
            <span>F11 for fullscreen</span>
            {fileType === 'image' && <span>+/- to zoom • R to rotate</span>}
            {(fileType === 'video' || fileType === 'audio') && <span>Space to play/pause</span>}
          </div>
        </div>
      </div>
    </div>
  );
};